import {
  BlockOutlined,
  Delete,
  Edit,
  TrendingUp,
  Undo,
  Visibility,
} from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { getQueryKey } from '@trpc/react-query';
import {
  createLpInvestmentEditRoute,
  createLpInvestmentViewRoute,
  createReversalRoute,
  createReversalViewRoute,
  LP_INVESTMENT_CREATE,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CalendarDayRange, SortOrder } from 'further-types/utils';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import { Column, Row } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import { ActionStatusLabels, FundingSourceLabels } from 'further-ui/labels';
import {
  numberToCurrencyString,
  numberToDisplayString,
  stringToColor,
} from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import api from 'lib/trpcClient';

const pageId = 'investments';
const orderByOptions = [
  'ledgerDate',
  'dealName',
  'totalInvestmentAmountTcy',
  'units',
  'averageUnitPriceTcy',
] as const;

type Props = {
  vehicleId: string;
};

const ListInvestmentsContent: React.FC<Props> = ({ vehicleId }) => {
  const deleteDisclosure = useDisclosure();
  const [canCreate, canDelete, canReverse] = useHasPermissions([
    RolePermission.LpCreateInvestment,
    RolePermission.LpDeleteInvestment,
    RolePermission.LpCreateReversal,
  ]);
  const markExcludedDisclosure = useDisclosure<{ id: string }>();
  const unmarkExcludedDisclosure = useDisclosure<{ id: string }>();

  const { filters, handleChangeFilters, pagination, isFiltersChangePending } =
    usePaginatedFilters<
      { searchQuery: string; dateRange: CalendarDayRange | undefined },
      (typeof orderByOptions)[number]
    >(pageId, {
      defaultFilters: {
        searchQuery: '',
        dateRange: undefined,
      },
      defaultOrder: {
        orderBy: 'ledgerDate',
        order: SortOrder.Desc,
      },
      orderByOptions,
    });

  const { data: vehicleHasExcludedInvestmentFeeBasis } =
    api.lpFeeTemplate.vehicleHasExcludedInvestmentFeeBasis.useQuery({
      vehicleId,
    });

  const [investments] = api.lpInvestment.list.useSuspenseQuery({
    vehicleId,
    ...pagination.properties,
    dateRange: filters.dateRange,
    searchQuery: filters.searchQuery,
  });

  const listInvestmentsQueryKey = getQueryKey(api.lpInvestment.list);
  const listLpFeesQueryKey = getQueryKey(api.lpFee.list);

  const deleteInvestment = api.lpInvestment.remove.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Investment deleted successfully.',
      invalidations: [listInvestmentsQueryKey, listLpFeesQueryKey],
    }),
  );

  const setExcludedFromFeeCalculations =
    api.lpInvestment.setExcludedFromFeeCalculations.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Investment exclusion updated.',
        invalidations: [listInvestmentsQueryKey, listLpFeesQueryKey],
      }),
    );

  const handleDeleteInvestment = () => {
    deleteInvestment.mutate({
      investmentId: deleteDisclosure.actionProps.id,
      vehicleId,
    });

    deleteDisclosure.onClose();
  };

  const handleMarkExcluded = () => {
    setExcludedFromFeeCalculations.mutate({
      investmentId: markExcludedDisclosure.actionProps.id,
      vehicleId,
      excluded: true,
    });
    markExcludedDisclosure.onClose();
  };

  const handleUnmarkExcluded = () => {
    setExcludedFromFeeCalculations.mutate({
      investmentId: unmarkExcludedDisclosure.actionProps.id,
      vehicleId,
      excluded: false,
    });
    unmarkExcludedDisclosure.onClose();
  };

  const columns: Array<TableColumn<(typeof investments.results)[number]>> = [
    {
      label: 'Deal name',
      key: 'dealName',
      width: 250,
      render: (row) => (
        <Row spacing="sm" centered>
          <ColorIndicator color={stringToColor(row.dealName)} />
          <Column>
            <Typography variant="inherit">{row.dealName}</Typography>
            <Typography variant="body2">
              {row.reversedAt ? 'Reversed' : ActionStatusLabels[row.status]}
            </Typography>
          </Column>
        </Row>
      ),
    },
    {
      label: 'Funding source',
      key: 'fundingSource',
      width: 180,
      render: (row) => FundingSourceLabels[row.fundingSource],
      disableSorting: true,
    },
    {
      label: 'Ledger date',
      key: 'ledgerDate',
      width: 140,
      render: (row) => row.ledgerDate.toReadable(),
    },

    {
      label: 'Total investment amount',
      key: 'totalInvestmentAmountTcy',
      width: 170,
      render: (row) =>
        numberToCurrencyString(row.totalInvestmentAmountTcy, {
          currency: row.currencyCode,
        }),
    },
    {
      label: 'Number of units',
      key: 'units',
      width: 130,
      render: (row) => numberToDisplayString(row.units),
    },
    {
      label: 'Average unit price',
      key: 'averageUnitPriceTcy',
      width: 140,
      render: (row) =>
        numberToCurrencyString(row.averageUnitPriceTcy, {
          currency: row.currencyCode,
        }),
    },
  ];

  return (
    <Page
      tabTitle="Investments: View Investments"
      spacing="lg"
      heading="Investments"
      headingIcon={TrendingUp}
      withHeadingDivider
      actions={[
        {
          label: 'Create investment',
          link: LP_INVESTMENT_CREATE,
          variant: 'contained',
          hidden: !canCreate,
        },
      ]}
    >
      <Column spacing="sm">
        <PageFilters justify="space-between">
          <Filter size="md">
            <SearchField
              autoSearchOnDebounce
              name="searchQuery"
              placeholder="Search by deal name"
              fullWidth
              value={filters.searchQuery}
              onSearch={(searchQuery) => {
                handleChangeFilters({ searchQuery });
              }}
              allowEmptyQuery
              iconPlacement="right"
            />
          </Filter>
          <Filter size="sm">
            <DateRangePicker
              dateRange={filters.dateRange}
              onDateRangeChange={(dateRange) => {
                handleChangeFilters({ dateRange });
              }}
              placeholder="Select date range"
            />
          </Filter>
        </PageFilters>
        <Table
          columns={columns}
          pagination={pagination}
          scrollable
          isLoading={isFiltersChangePending || pagination.isPaginationChanging}
          rows={investments?.results ?? []}
          rowsTotalCount={investments?.totalCount ?? 0}
          onRowClickHref={(row) => createLpInvestmentViewRoute(row.id)}
          actions={[
            {
              label: 'View',
              icon: Visibility,
              link: (row) => createLpInvestmentViewRoute(row.id),
            },
            {
              label: 'Edit',
              icon: Edit,
              link: (row) => createLpInvestmentEditRoute(row.id),
              hidden: !canCreate,
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.canModifyReason : undefined,
            },
            {
              label: 'Mark as excluded investment',
              icon: BlockOutlined,
              onClick: (row) => {
                markExcludedDisclosure.stageAction({ id: row.id });
              },
              hidden: (row) =>
                !canCreate ||
                !vehicleHasExcludedInvestmentFeeBasis ||
                !!row.excludedFromFeeCalculations,
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.canModifyReason : undefined,
            },
            {
              label: 'Unmark as excluded investment',
              icon: BlockOutlined,
              onClick: (row) => {
                unmarkExcludedDisclosure.stageAction({ id: row.id });
              },
              hidden: (row) => !canCreate || !row.excludedFromFeeCalculations,
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.canModifyReason : undefined,
            },
            {
              label: (row) => (row.reversedAt ? 'View reversal' : 'Reverse'),
              icon: Undo,
              link: (row) =>
                row.reversedAt
                  ? createReversalViewRoute(row.id, 'investment')
                  : createReversalRoute(row.id, 'investment'),
              hidden: (row) => !row.reversedAt && !canReverse,
              disabled: (row) => !row.reversedAt && !row.canReverse,
              tooltip: (row) => {
                if (row.reversedAt) return undefined;
                if (!row.canReverse) return row.canReverseReason;
                return undefined;
              },
            },
            {
              label: 'Delete',
              icon: Delete,
              onClick: (row) => deleteDisclosure.stageAction({ id: row.id }),
              hidden: !canDelete,
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.canModifyReason : undefined,
            },
          ]}
        />
      </Column>
      <ConfirmDialog
        maxWidth="sm"
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Are you sure you wish to delete this action?"
        content="This cannot be undone and all information relating to the action will be lost. If you have draft equalisations they will also be deleted."
        actions={{
          confirm: {
            label: 'Delete investment',
            onClick: handleDeleteInvestment,
          },
          cancel: {
            label: 'Go back',
            onClick: deleteDisclosure.onClose,
          },
        }}
      />
      <ConfirmDialog
        maxWidth="sm"
        onClose={markExcludedDisclosure.onClose}
        open={markExcludedDisclosure.isOpen}
        title="Mark as excluded investment"
        content={
          <Typography variant="body2" component="span">
            Excluded investments are deducted from management fee calculations{' '}
            (if the exclusion fee basis is set in Fund Setup). Are you sure you
            wish to continue?
          </Typography>
        }
        actions={{
          confirm: {
            label: 'Submit',
            onClick: handleMarkExcluded,
          },
          cancel: {
            label: 'Cancel',
            onClick: markExcludedDisclosure.onClose,
          },
        }}
      />
      <ConfirmDialog
        maxWidth="sm"
        onClose={unmarkExcludedDisclosure.onClose}
        open={unmarkExcludedDisclosure.isOpen}
        title="Unmark as excluded investment"
        content={
          <Typography variant="body2" component="span">
            This investment will no longer be excluded from management fee
            calculations. Are you sure you wish to continue?
          </Typography>
        }
        actions={{
          confirm: {
            label: 'Submit',
            onClick: handleUnmarkExcluded,
          },
          cancel: {
            label: 'Cancel',
            onClick: unmarkExcludedDisclosure.onClose,
          },
        }}
      />
    </Page>
  );
};

const ListInvestments: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  if (!selectedVehicleId) {
    return null;
  }
  return <ListInvestmentsContent vehicleId={selectedVehicleId} />;
};

export default ListInvestments;
